import { loadPnlVersions } from 'src/components/star-grid/services/loadPnlVersions';
import { PNL_VERSION_PAGE_SIZE } from 'src/components/star-grid/starGridConstants';
import { pnlQueryPayload } from 'src/components/star-grid/utils/pnlQueryRequest';
import { PnlDataVersion, PnlDeckGroup } from '../reportSelectors.types';
import { PnlReportStatusError } from '../utils/pnlReportStatusError';
import { fetchAuthSession } from 'aws-amplify/auth';
import { STAR_GRID_API_BASE_URL } from '../starGridConstants';
import {
  CreatePnlReportResponse,
  DataSyncCreateResponse,
  DataSyncJob,
  DataSyncRequest,
  PnlConfigResponse,
  PnlHierarchyGroup,
  PnlHierarchyOptions,
  PnlHierarchyTree,
  PnlOpStat,
  PnlQueryRequest,
  PnlQueryResponse,
  PnlReportConfig,
  PnlReportRequest,
  PnlReportSummary,
  StarGridHealth,
  StarGridHello,
  StarGridInfra
} from '../starGridTypes';

/** Extract the FastAPI `detail` message from an error response body, if present. */
async function errorDetail(response: Response, path: string): Promise<string> {
  try {
    const body: { detail?: unknown } = await response.json();
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'error' in detail && typeof detail.error === 'string') {
      return detail.error;
    }
    if (Array.isArray(detail)) {
      return (
        detail
          .map((item: unknown) => {
            if (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string') return item.msg;
            return '';
          })
          .filter(Boolean)
          .join('; ') || `Invalid request (${response.status})`
      );
    }
  } catch {
    // Non-JSON body — fall through to the status line.
  }
  return `StarGrid API ${path} failed: ${response.status} ${response.statusText}`;
}

/**
 * Thrown when a pivot query would return more rows than the API guardrail permits (HTTP 413).
 * Carries the server-reported estimate + hint so the UI can nudge the user toward narrowing
 * the query (fewer dims, more filters) rather than showing a generic error.
 */
export class QueryTooLargeError extends Error {
  readonly estimatedRows: number;
  readonly rowLimit: number;
  readonly hint: string;
  constructor(estimatedRows: number, rowLimit: number, hint: string) {
    super(`Result too large (~${estimatedRows.toLocaleString()} rows > ${rowLimit.toLocaleString()} row limit): ${hint}`);
    this.name = 'QueryTooLargeError';
    this.estimatedRows = estimatedRows;
    this.rowLimit = rowLimit;
    this.hint = hint;
  }
}

/**
 * Thrown when the StarGrid API returns 401 — the caller's Cognito session has expired
 * (refresh token past its TTL, or user signed out in another tab) and cannot be silently
 * refreshed. Callers / AuthContextProvider should catch this and trigger the SSO re-signin
 * flow (`signInWithRedirect`) rather than surfacing a generic error toast.
 */
export class AuthExpiredError extends Error {
  readonly path: string;
  constructor(path: string) {
    super(`Session expired while calling ${path} — sign in again.`);
    this.name = 'AuthExpiredError';
    this.path = path;
  }
}

/** Resolve the caller's FinSuite Cognito access token for the Authorization header. */
async function bearerToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error('No active session — sign in to call the StarGrid API.');
  }
  return token;
}

/**
 * Attach the caller's Cognito bearer to a StarGrid API request and translate 401 into
 * a typed :class:`AuthExpiredError` so the UI layer can drive re-signin. Amplify's
 * `fetchAuthSession()` silently refreshes the access token via the refresh token; a 401
 * therefore means the refresh token itself is gone, not just an expired access token.
 */
async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const token = await bearerToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
    Authorization: `Bearer ${token}`
  };
  const response = await fetch(`${STAR_GRID_API_BASE_URL}${path}`, { ...init, headers });
  if (response.status === 401) {
    throw new AuthExpiredError(path);
  }
  return response;
}

/** GET a JSON resource from the StarGrid API with the caller's Cognito bearer token. */
async function authedGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await authedFetch(path, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error(await errorDetail(response, path));
  }
  return (await response.json()) as T;
}

/** POST JSON to the StarGrid API with the caller's Cognito bearer token. */
async function authedPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const response = await authedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal
  });
  if (!response.ok) {
    throw new Error(await errorDetail(response, path));
  }
  return (await response.json()) as T;
}

/** Typed REST client for the StarGrid API. */
export async function getStarGridHealth(): Promise<StarGridHealth> {
  const response = await fetch(`${STAR_GRID_API_BASE_URL}/health`, {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`StarGrid API health check failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as StarGridHealth;
}

/**
 * Calls the auth-gated `GET /hello` with the caller's FinSuite Cognito access
 * token as a bearer credential. The StarGrid API validates it against FinSuite's
 * Cognito JWKS (cross-account).
 */
export async function getStarGridHello(): Promise<StarGridHello> {
  return authedGet<StarGridHello>('/hello');
}

/** Auth-gated StarRocks infrastructure descriptor (API reads it from SSM). */
export async function getStarGridInfra(): Promise<StarGridInfra> {
  return authedGet<StarGridInfra>('/infra');
}

/** DELETE a StarGrid API resource with the caller's Cognito bearer token (204 → void). */
async function authedDelete(path: string): Promise<void> {
  const response = await authedFetch(path, { method: 'DELETE' });
  if (!response.ok) {
    throw new Error(await errorDetail(response, path));
  }
}

// ─── PnL Reports (persisted) ──────────────────────────────────────────────────

/** Auth-gated: the global PnL picker config (scenarios, dimensions, measures) from S3. */
export async function getPnlConfig(): Promise<PnlConfigResponse> {
  return authedGet<PnlConfigResponse>('/pnl-reports/config');
}

/** Auth-gated: rollup hierarchies (registry ∩ pnl_paths), grouped by dimension. */
export async function listPnlHierarchies(signal?: AbortSignal): Promise<PnlHierarchyGroup[]> {
  return authedGet<PnlHierarchyGroup[]>('/pnl-hierarchies', signal);
}

/** Auth-gated: the leaf option tree of one hierarchy (value + root→leaf path), for the picker. */
export async function listHierarchyOptions(hierarchyId: string, signal?: AbortSignal): Promise<PnlHierarchyOptions> {
  return authedGet<PnlHierarchyOptions>(`/pnl-hierarchies/${encodeURIComponent(hierarchyId)}/options`, signal);
}

/**
 * Auth-gated: the WHOLE hierarchy as a nested member tree — the subset editor's dataset.
 *
 * Uncapped, unlike `listHierarchyOptions`: the editor's Keep/Leaves operations are set algebra over
 * this tree, so a truncated tree would answer them wrongly rather than slowly. Fetch once per
 * hierarchy and run the operations client-side.
 */
export async function getHierarchyTree(hierarchyId: string, signal?: AbortSignal): Promise<PnlHierarchyTree> {
  return authedGet<PnlHierarchyTree>(`/pnl-hierarchies/${encodeURIComponent(hierarchyId)}/tree`, signal);
}

/** Auth-gated: list the persisted PnL reports (for the Reports nav section). */
export async function listPnlReports(): Promise<PnlReportSummary[]> {
  return authedGet<PnlReportSummary[]>('/pnl-reports');
}

/** Auth-gated: create a PnL report and (synchronously) generate its cube. */
export async function createPnlReport(request: PnlReportRequest): Promise<CreatePnlReportResponse> {
  return authedPost<CreatePnlReportResponse>('/pnl-reports', request);
}

/** Auth-gated: the saved definition of a PnL report (for re-open / detail view). */
export async function getPnlReportConfig(reportId: number, signal?: AbortSignal): Promise<PnlReportConfig> {
  return authedGet<PnlReportConfig>(`/pnl-reports/${reportId}/config`, signal);
}

/** Auth-gated: run an interactive pivot query over a PnL report's cube.
 *
 * Throws :class:`QueryTooLargeError` on HTTP 413 so the UI can render a "Result too large"
 * banner with the server's row estimate + hint instead of a generic API error.
 */
export async function queryPnlReport(reportId: number, request: PnlQueryRequest, signal?: AbortSignal): Promise<PnlQueryResponse> {
  const path = `/pnl-reports/${reportId}/query`;
  const response = await authedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pnlQueryPayload(request)),
    signal
  });
  if (response.status === 409) throw new PnlReportStatusError(await errorDetail(response, path));
  if (response.status === 413) {
    try {
      const body = (await response.json()) as { detail?: { estimated_rows?: number; row_limit?: number; hint?: string } };
      const d = body.detail ?? {};
      throw new QueryTooLargeError(Number(d.estimated_rows ?? 0), Number(d.row_limit ?? 0), String(d.hint ?? 'Add filters or fewer dimensions'));
    } catch (err) {
      if (err instanceof QueryTooLargeError) throw err;
      throw new QueryTooLargeError(0, 0, 'Add filters or fewer dimensions');
    }
  }
  if (!response.ok) {
    throw new Error(await errorDetail(response, path));
  }
  return (await response.json()) as PnlQueryResponse;
}

/** Auth-gated: soft-delete a PnL report (and drop its cube). */
export async function deletePnlReport(reportId: number): Promise<void> {
  return authedDelete(`/pnl-reports/${reportId}`);
}

/** Auth-gated: recent per-request step-timing rows for this report (newest first). */
export async function getReportStats(reportId: number, limit = 50): Promise<PnlOpStat[]> {
  return authedGet<PnlOpStat[]>(`/pnl-reports/${reportId}/stats?limit=${limit}`);
}

// ─── Data Sync ────────────────────────────────────────────────────────────────

/** Start a full-table or selected-month sync from the source. */
export async function createDataSync(request: DataSyncRequest): Promise<DataSyncCreateResponse> {
  return authedPost<DataSyncCreateResponse>('/data-sync', request);
}

/** Newest-first sync history for the Admin table. */
export async function listDataSyncJobs(limit = 50, signal?: AbortSignal): Promise<DataSyncJob[]> {
  return authedGet<DataSyncJob[]>(`/data-sync?limit=${limit}`, signal);
}

/** One sync job's live state — polled while status is STARTING / RUNNING. */
export async function getDataSyncJob(jobId: number): Promise<DataSyncJob> {
  return authedGet<DataSyncJob>(`/data-sync/${jobId}`);
}

/** Available data versions, including the backend-provided Live entry. */
export const listPnlVersions = (onIncomplete?: () => void): Promise<PnlDataVersion[]> =>
  loadPnlVersions((offset) => authedGet<PnlDataVersion[]>(`/pnl-versions?limit=${PNL_VERSION_PAGE_SIZE}&offset=${offset}`), onIncomplete);
/** Available report definitions grouped by report family and deck type. */
export const listPnlDecks = (): Promise<PnlDeckGroup[]> => authedGet<PnlDeckGroup[]>('/pnl-decks');
